// The company's RFI cover template (back/routes/rfiTemplate): the company
// block, the defaults, the draft line, the field labels, the colour. Empty
// fields fall back to the company record and then to the neutral default,
// shown as placeholders so a person sees what an empty field will print.
// Read-only without rfiTemplateManage (the server says which).
//
// READ FROM AN EXAMPLE RFI fills the form from an RFI the company already
// sent - letterhead, logo, field wording, defaults, colour, page size
// (back/rfi/learnTemplate). It fills the form only; Save keeps it. The
// example is filed in Document Control on the project picked on the page.
import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Divider, Drawer, Form, Input, Row, Select, Space, Spin, Tag, Tooltip, Typography, message } from "antd";
import { EyeOutlined, FileSearchOutlined, SaveOutlined, UploadOutlined } from "@ant-design/icons";
import {
  fetchRfiTemplateLogo, fetchRfiTemplatePreview, getRfiTemplate, learnRfiTemplate, saveRfiTemplate, uploadRfiTemplateLogo,
} from "api/rfi/rfiApi";

const { Text } = Typography;

// What a found item is called on this page.
const FOUND_NAMES = {
  "company.name": "Company name", "company.address": "Address", "company.phone": "Phone", "company.logo": "Logo",
  "defaults.to": "To", "defaults.from": "From", "defaults.costImpact": "Cost impact", "defaults.scheduleImpact": "Schedule impact",
  "style.accent": "Accent colour", pageSize: "Page size", fieldOrder: "Fields on the cover",
};

const LABEL_NAMES = {
  revision: "Revision", status: "Status", to: "To", from: "From", dateInitiated: "Date initiated", dueDate: "Due date",
  location: "Location", reference: "Reference", costImpact: "Cost impact", scheduleImpact: "Schedule impact",
  specSection: "Spec section", costCode: "Cost code", drawingNumbers: "Drawing numbers", linkedDrawings: "Linked drawings",
  receivedFrom: "Received from", copiesTo: "Copies to",
};

const empty = () => ({
  company: { name: "", address: ["", "", ""], phone: "", logoKey: "" },
  defaults: { to: "", from: "", costImpact: "", scheduleImpact: "", draftText: "" },
  fieldLabels: {}, fieldOrder: [], style: { accent: "" }, pageSize: "", naming: { file: "" },
});

export default function RfiTemplateDrawer({ projectId, onClose }) {
  const [data, setData] = useState(null);
  const [t, setT] = useState(empty);
  const [busy, setBusy] = useState("");
  const [learned, setLearned] = useState(null);
  const [logoSrc, setLogoSrc] = useState("");
  const exampleRef = useRef(null);
  const logoRef = useRef(null);
  const logoKey = t.company.logoKey || "";

  useEffect(() => {
    getRfiTemplate()
      .then((r) => {
        setData(r);
        const s = r.template || {};
        const e = empty();
        setT({
          company: { ...e.company, ...(s.company || {}), address: [0, 1, 2].map((k) => ((s.company && s.company.address) || [])[k] || "") },
          defaults: { ...e.defaults, ...(s.defaults || {}) },
          fieldLabels: { ...(s.fieldLabels || {}) },
          fieldOrder: [...(s.fieldOrder || [])],
          style: { ...e.style, ...(s.style || {}) },
          pageSize: s.pageSize || "",
          naming: { ...e.naming, ...(s.naming || {}) },
        });
      })
      .catch((e) => message.error(e.message || "Could not load the template"));
  }, []);

  // The logo the cover will print: the one chosen here, else the company's.
  useEffect(() => {
    if (!data) return undefined;
    let href = "";
    let live = true;
    fetchRfiTemplateLogo(logoKey)
      .then((blob) => { if (!live) return; href = URL.createObjectURL(blob); setLogoSrc(href); })
      .catch(() => { if (live) setLogoSrc(""); });
    return () => { live = false; if (href) URL.revokeObjectURL(href); };
  }, [data, logoKey]);

  if (!data) return <Drawer open width={720} title="Company RFI template" onClose={onClose}><Spin /></Drawer>;
  const uses = data.uses || {};
  const ro = !data.canManage;
  const setIn = (part, key, v) => setT((x) => ({ ...x, [part]: { ...x[part], [key]: v } }));
  const field = (part, key, label, placeholder) => (
    <Form.Item label={label} style={{ marginBottom: 10 }}>
      <Input disabled={ro} value={t[part][key] || ""} placeholder={placeholder} onChange={(e) => setIn(part, key, e.target.value)} />
    </Form.Item>
  );

  // Lay what was read over the form: a value read replaces the form's, an
  // empty one leaves it.
  const learn = async (file) => {
    if (!file) return;
    setBusy("learn");
    try {
      const r = await learnRfiTemplate(projectId, file);
      const p = r.proposal || {};
      const pc = p.company || {};
      setT((x) => ({
        ...x,
        company: {
          ...x.company,
          ...(pc.name ? { name: pc.name } : {}),
          ...(pc.address && pc.address.length ? { address: [0, 1, 2].map((k) => pc.address[k] || "") } : {}),
          ...(pc.phone ? { phone: pc.phone } : {}),
          ...(pc.logoKey ? { logoKey: pc.logoKey } : {}),
        },
        defaults: { ...x.defaults, ...Object.fromEntries(Object.entries(p.defaults || {}).filter(([, v]) => v)) },
        fieldLabels: { ...x.fieldLabels, ...(p.fieldLabels || {}) },
        fieldOrder: p.fieldOrder && p.fieldOrder.length ? p.fieldOrder : x.fieldOrder,
        style: { ...x.style, ...(p.style && p.style.accent ? { accent: p.style.accent } : {}) },
        pageSize: p.pageSize || x.pageSize,
      }));
      setLearned({ ...r, fileName: file.name });
      message.success("Read - check the fields below, then Save");
    } catch (e) {
      message.error(e.message || "Could not read that RFI");
    } finally { setBusy(""); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy("logo");
    try {
      const r = await uploadRfiTemplateLogo(file);
      setT((x) => ({ ...x, company: { ...x.company, logoKey: r.logoKey } }));
    } catch (e) {
      message.error(e.message || "Could not keep the logo");
    } finally { setBusy(""); }
  };

  const save = async () => {
    setBusy("save");
    try {
      await saveRfiTemplate({ ...t, company: { ...t.company, address: t.company.address.filter((l) => l.trim()) } });
      message.success("Template saved - new builds use it");
    } catch (e) {
      message.error(e.message || "Could not save the template");
    } finally { setBusy(""); }
  };
  const preview = async () => {
    setBusy("preview");
    try {
      const href = URL.createObjectURL(await fetchRfiTemplatePreview());
      window.open(href, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch (e) {
      message.error(e.message || "Could not draw the preview");
    } finally { setBusy(""); }
  };

  return (
    <Drawer
      open width={Math.min(760, window.innerWidth)} title="Company RFI template" onClose={onClose}
      extra={(
        <Space>
          <Button icon={<EyeOutlined />} onClick={preview} loading={busy === "preview"}>Preview saved cover</Button>
          {!ro && <Button type="primary" icon={<SaveOutlined />} onClick={save} loading={busy === "save"}>Save</Button>}
        </Space>
      )}
    >
      {ro && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Only someone with the RFI template permission (or a company admin) can change this." />}
      {!ro && (
        <div style={{ border: "1px dashed #d9d9d9", borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <Space wrap align="center">
            <Tooltip title={projectId ? "" : "Pick the project the RFI was written for first - the example is filed in its Document Control"}>
              <Button icon={<FileSearchOutlined />} loading={busy === "learn"} disabled={!projectId} onClick={() => exampleRef.current && exampleRef.current.click()}>
                Read from an example RFI
              </Button>
            </Tooltip>
            <Text type="secondary">A PDF of an RFI your company already sent: its letterhead, logo, field names and colour fill the form.</Text>
          </Space>
          <input
            ref={exampleRef} type="file" accept="application/pdf,.pdf" style={{ display: "none" }}
            onChange={(e) => { const file = e.target.files && e.target.files[0]; e.target.value = ""; learn(file); }}
          />
          {learned && (
            <div style={{ marginTop: 10 }}>
              <Alert
                type="success" showIcon
                message={`Read from ${learned.fileName} - check each field, then Save`}
                description={(
                  <div>
                    {(learned.found || []).map((f) => (
                      <div key={f.field}>
                        <Text strong>{FOUND_NAMES[f.field] || (f.field.startsWith("fieldLabels.") ? `Label for ${LABEL_NAMES[f.field.slice(12)] || f.field.slice(12)}` : f.field)}:</Text>{" "}
                        {f.value} <Text type="secondary">({f.from})</Text>
                      </div>
                    ))}
                    {learned.filed && <Text type="secondary">Filed in Document Control as {learned.filed.name}.</Text>}
                    {learned.fileError && <Text type="danger">{learned.fileError}</Text>}
                  </div>
                )}
              />
              {(learned.notes || []).length > 0 && (
                <Alert type="warning" showIcon style={{ marginTop: 8 }} message="Not found" description={learned.notes.map((n) => <div key={n}>{n}</div>)} />
              )}
            </div>
          )}
        </div>
      )}
      <Alert
        type="info" style={{ marginBottom: 12 }}
        message="Every RFI your company writes is built on this cover. Leave a field empty to use what the placeholder shows."
      />
      <Form layout="vertical">
        <Divider orientation="left">Company block</Divider>
        {field("company", "name", "Company name", (uses.company && uses.company.name) || "")}
        {[0, 1, 2].map((k) => (
          <Form.Item key={k} label={k === 0 ? "Address" : undefined} style={{ marginBottom: 6 }}>
            <Input
              disabled={ro} value={t.company.address[k]}
              placeholder={((uses.company && uses.company.address) || [])[k] || ""}
              onChange={(e) => setIn("company", "address", t.company.address.map((l, j) => (j === k ? e.target.value : l)))}
            />
          </Form.Item>
        ))}
        {field("company", "phone", "Phone", (uses.company && uses.company.phone) || "")}
        <Form.Item label="Logo" style={{ marginBottom: 10 }} extra={logoKey ? "Chosen here" : "From the company profile"}>
          <Space wrap align="center">
            <div style={{ height: 56, minWidth: 56, maxWidth: 200, border: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
              {logoSrc ? <img alt="Logo" src={logoSrc} style={{ maxHeight: 48, maxWidth: 190 }} /> : <Text type="secondary">No logo</Text>}
            </div>
            {!ro && <Button size="small" icon={<UploadOutlined />} loading={busy === "logo"} onClick={() => logoRef.current && logoRef.current.click()}>Upload logo</Button>}
            {!ro && logoKey && <Button size="small" onClick={() => setT((x) => ({ ...x, company: { ...x.company, logoKey: "" } }))}>Use the company profile logo</Button>}
          </Space>
          <input
            ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }}
            onChange={(e) => { const file = e.target.files && e.target.files[0]; e.target.value = ""; uploadLogo(file); }}
          />
        </Form.Item>
        <Divider orientation="left">Defaults on every RFI</Divider>
        <Row gutter={12}>
          <Col xs={24} md={12}>{field("defaults", "to", "To", uses.defaults && uses.defaults.to)}</Col>
          <Col xs={24} md={12}>{field("defaults", "from", "From", uses.defaults && uses.defaults.from)}</Col>
          <Col xs={12}>{field("defaults", "costImpact", "Cost impact", uses.defaults && uses.defaults.costImpact)}</Col>
          <Col xs={12}>{field("defaults", "scheduleImpact", "Schedule impact", uses.defaults && uses.defaults.scheduleImpact)}</Col>
        </Row>
        {field("defaults", "draftText", "Draft line (red, on the cover)", uses.defaults && uses.defaults.draftText)}
        <Divider orientation="left">Look</Divider>
        <Row gutter={12}>
          <Col xs={12}>
            <Form.Item label="Accent colour" style={{ marginBottom: 10 }}>
              <Input disabled={ro} value={t.style.accent} placeholder={uses.accent} onChange={(e) => setIn("style", "accent", e.target.value)} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item label="Cover page size" style={{ marginBottom: 10 }}>
              <Select
                disabled={ro} value={t.pageSize}
                options={[{ value: "", label: `Default (${uses.pageSize || "letter"})` }, { value: "letter", label: "Letter" }, { value: "a4", label: "A4" }]}
                onChange={(v) => setT((x) => ({ ...x, pageSize: v }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="File name" extra="Fields: {sheets}, {subject}, {rfiNumber}, {project.number}" style={{ marginBottom: 10 }}>
          <Input disabled={ro} value={t.naming.file} placeholder={uses.naming} onChange={(e) => setIn("naming", "file", e.target.value)} />
        </Form.Item>
        <Divider orientation="left">Fields on the cover</Divider>
        <Space wrap style={{ marginBottom: 8 }}>
          {(t.fieldOrder.length ? t.fieldOrder : uses.fieldOrder || []).map((k, i) => (
            <Tag key={k} closable={!ro} onClose={(e) => {
              e.preventDefault();
              const list = t.fieldOrder.length ? t.fieldOrder : uses.fieldOrder || [];
              setT((x) => ({ ...x, fieldOrder: list.filter((y) => y !== k) }));
            }}>
              {i + 1}. {t.fieldLabels[k] || LABEL_NAMES[k] || k}
            </Tag>
          ))}
          {!ro && t.fieldOrder.length > 0 && <Button size="small" onClick={() => setT((x) => ({ ...x, fieldOrder: [] }))}>Standard list</Button>}
        </Space>
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Printed two to a row in this order. Reading an example RFI sets them to the fields it has.
        </Text>
        <Divider orientation="left">Field labels, in your company's wording</Divider>
        <Row gutter={12}>
          {(uses.fieldLabelKeys || []).map((k) => (
            <Col xs={12} md={8} key={k}>
              <Form.Item label={LABEL_NAMES[k] || k} style={{ marginBottom: 8 }}>
                <Input
                  size="small" disabled={ro} value={t.fieldLabels[k] || ""} placeholder={LABEL_NAMES[k] || k}
                  onChange={(e) => setT((x) => ({ ...x, fieldLabels: { ...x.fieldLabels, [k]: e.target.value } }))}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Form>
    </Drawer>
  );
}
